import type { ReactNode } from 'react';
import { DOCUMENT_TYPE_LABELS } from '@/features/document-templates/utils/documentTypeLabels';
import type { GeneratedDocument } from '../../types/agreementDocuments.types';
import { formatOccurredAt } from '../../utils/timelineEntryLabels';
import {
    failureDetail,
    failureSummary,
    formatByteSize,
    requesterLabel,
    templateVersionLabel,
    triggerLabel,
} from '../../utils/generatedDocumentDisplay';
import { DocumentStatusCell } from './DocumentStatusCell';

const TEMPLATE_PENDING_COPY: Record<GeneratedDocument['status'], string> = {
    pending: 'Chosen when generated',
    generated: '—',
    failed: 'Not resolved',
};

interface Props {
    document: GeneratedDocument;
    now: number;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="contents">
            <dt className="text-mp-muted sm:col-span-4">{label}</dt>
            <dd className="sm:col-span-8">{children}</dd>
        </div>
    );
}

export function DocumentProvenance({ document, now }: Props) {
    const detail = failureDetail(document.failure);

    return (
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-12">
            <Row label="Document">{DOCUMENT_TYPE_LABELS[document.documentType]}</Row>
            <Row label="Status"><DocumentStatusCell document={document} now={now} showFailure={false} /></Row>
            {document.failure && (
                <Row label="Reason">
                    {failureSummary(document.failure, document.id)}
                    {detail && <span className="block text-mp-muted">{detail}</span>}
                </Row>
            )}
            <Row label="Template">{document.template ? templateVersionLabel(document.template) : TEMPLATE_PENDING_COPY[document.status]}</Row>
            <Row label="Trigger">{triggerLabel(document.trigger)}</Row>
            <Row label="Requested by">{requesterLabel(document.requestedBy)}</Row>
            <Row label="Requested">{formatOccurredAt(document.createdAt)}</Row>
            {document.generatedAt && <Row label="Generated">{formatOccurredAt(document.generatedAt)}</Row>}
            {document.failedAt && <Row label="Failed">{formatOccurredAt(document.failedAt)}</Row>}
            <Row label="Attempts">{document.attempts}</Row>
            {document.checksumSha256 && (
                <Row label="File">
                    PDF, {formatByteSize(document.byteSize)}
                    <code className="mt-1 block break-all text-xs text-mp-muted">SHA-256 {document.checksumSha256}</code>
                </Row>
            )}
            <Row label="Empty fields">
                {document.emptyVariables.length > 0 ? (
                    <code className="text-xs">{document.emptyVariables.join(', ')}</code>
                ) : (
                    'None'
                )}
            </Row>
        </dl>
    );
}
